import { useContext, useRef } from "react";
import { Box, Typography } from "@mui/material";
import type { CustomSeriesRenderItem, EChartsOption } from "echarts";
import { RopChart } from "../../../../main/components/RopChart";
import { MainContext } from "../../../../main/context/MainContextProvider";
import { RopCard1103ImpactoNaMetaTaticoAggregated } from "../../types";

export type ImpactoNaMetaTacticalChartProps = {
    data: RopCard1103ImpactoNaMetaTaticoAggregated;
};

export default function ImpactoNaMetaTacticalChart({
    data
}: ImpactoNaMetaTacticalChartProps) {
    const chartRef = useRef(null);
    const { theme } = useContext(MainContext);

    const getMonthName = (monthKey: number): string => {
        const monthNames = [
            "janeiro", "fevereiro", "março", "abril", "maio", "junho",
            "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"
        ];
        return monthNames[monthKey] || "";
    };

    const generateMonthsFromJanuary = () => {
        const months = [];
        const now = new Date();
        const currentYear = now.getFullYear();

        for (let i = 0; i < 12; i++) {
            const date = new Date(currentYear, i, 1);
            const monthKey = date.getMonth();
            months.push(getMonthName(monthKey));
        }

        return months;
    };

    const allMonths = generateMonthsFromJanuary();
    const currentMonth = new Date().getMonth();

    // Calcular valores mensais individuais a partir dos valores acumulados
    const realMesValues = allMonths.map((monthKey, index) => {
        // Não mostrar barras para meses futuros
        if (index > currentMonth) return 0;

        const currentMonthData = data.monthlyData[monthKey];
        if (!currentMonthData) return 0;

        const currentCumulative = currentMonthData.generatedEnergy ?? 0;

        if (index === 0) {
            return currentCumulative;
        }

        const previousMonthKey = allMonths[index - 1];
        const previousMonthData = data.monthlyData[previousMonthKey];
        const previousCumulative = previousMonthData?.generatedEnergy ?? 0;

        return currentCumulative - previousCumulative;
    });

    const metaMesValues = allMonths.map((monthKey, index) => {
        const currentMonthData = data.monthlyData[monthKey];
        if (!currentMonthData) return 0;

        const currentCumulative = currentMonthData.target ?? 0;

        if (index === 0) {
            return currentCumulative;
        }

        const previousMonthKey = allMonths[index - 1];
        const previousMonthData = data.monthlyData[previousMonthKey];
        const previousCumulative = previousMonthData?.target ?? 0;

        return currentCumulative - previousCumulative;
    });

    // Real Acumulado = cumulative up to previous month
    const realAcumuladoValues = allMonths.map((monthKey, index) => {
        // Não mostrar barras para meses futuros
        if (index > currentMonth) return 0;

        const monthData = data.monthlyData[monthKey];
        if (!monthData) return 0;
        const totalCumulative = monthData.generatedEnergy ?? 0;
        const individualMonth = realMesValues[index] ?? 0;
        return totalCumulative - individualMonth;
    });

    // Meta Acumulada = cumulative target up to previous month
    const metaAcumuladaValues = allMonths.map((monthKey) => {
        const monthData = data.monthlyData[monthKey];
        if (!monthData) return 0;
        const totalCumulative = monthData.target ?? 0;
        const individualMonth = metaMesValues[allMonths.indexOf(monthKey)] ?? 0;
        return totalCumulative - individualMonth;
    });

    // Meta Acumulada (full cumulative) for the dashed lines
    const targetValues = allMonths.map((monthKey) =>
        data.monthlyData[monthKey]?.target ?? 0
    );

    const plus5Values = targetValues.map((target) => target > 0 ? target * 1.05 : 0);
    const minus5Values = targetValues.map((target) => target > 0 ? target * 0.95 : 0);

    const renderMeta = (color: string): CustomSeriesRenderItem => (_params, api) => {
        const xValue = api.value(0);
        const yValue = api.value(1);

        const coord = api.coord([xValue, yValue]);

        const categoryWidth = (api.size!([1, 0]) as number[])[0]

        const lineWidth = categoryWidth;

        return {
            type: "line",
            shape: {
                x1: coord[0] - lineWidth / 2,
                y1: coord[1],
                x2: coord[0] + lineWidth / 2,
                y2: coord[1]
            },
            style: {
                stroke: color,
                lineWidth: 2.5,
                lineDash: [4.5, 4.5]
            }
        };
    };

    const chartOption: EChartsOption = {
        color: ["#3B6BA5", "#528AD1"],
        backgroundColor: "transparent",
        grid: {
            left: "3%",
            right: "3%",
            bottom: "10%",
            top: "15%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: allMonths,
            axisLine: {
                lineStyle: {
                    color: theme?.cardBorderColor || "#E5E7EB",
                },
            },
            axisLabel: {
                color: theme?.primaryTextColor || "#6B7280",
                fontSize: 11,
                interval: 0,
                rotate: allMonths.length > 6 ? 45 : 0,
                formatter: (value: string) => {
                    if (allMonths.length > 8) {
                        return value.substring(0, 3);
                    }
                    return value;
                },
            },
            splitLine: {
                show: false,
            },
        },
        yAxis: {
            type: "value",
            name: "MWh",
            nameTextStyle: {
                color: theme?.primaryTextColor || "#6B7280",
                fontSize: 11,
            },
            axisLine: {
                lineStyle: {
                    color: theme?.cardBorderColor || "#E5E7EB",
                },
            },
            axisLabel: {
                color: theme?.primaryTextColor || "#6B7280",
                fontSize: 12,
                formatter: (value: number) => value >= 1000 ? `${value / 1000}k` : String(value),
            },
            splitLine: {
                show: true,
                lineStyle: {
                    color: theme?.cardBorderColor || "#374151",
                    type: "dashed",
                    opacity: 0.5,
                },
            },
        },
        series: [
            // Real Acumulado - bottom portion of stacked bar
            {
                name: "Real Acumulado",
                type: "bar",
                stack: "real",
                data: realAcumuladoValues,
                barWidth: "97%",
                itemStyle: {
                    color: "#3B6BA5",
                    borderRadius: [0, 0, 0, 0],
                },
                label: {
                    show: false,
                },
            },
            // Real Mês - top portion of stacked bar
            {
                name: "Real Mês",
                type: "bar",
                stack: "real",
                data: realMesValues,
                barWidth: "97%",
                itemStyle: {
                    color: "#7EB3E0",
                    borderRadius: [2, 2, 0, 0],
                },
                label: {
                    show: false,
                },
            },
            // META Acumulada (traços individuais)
            {
                name: "Meta Acumulada",
                type: "custom",
                data: targetValues,
                z: 10,
                renderItem: renderMeta("#FFF")
            },

            // +5%
            {
                name: "+5%",
                type: "custom",
                data: plus5Values,
                z: 10,
                renderItem: renderMeta("#10B981")
            },

            // -5%
            {
                name: "-5%",
                type: "custom",
                data: minus5Values,
                z: 10,
                renderItem: renderMeta("#EF4444")
            }
        ],
        legend: {
            show: false,
        },
        tooltip: {
            trigger: "axis",
            backgroundColor: theme?.backgroundColor || "#1F2937",
            borderColor: theme?.cardBorderColor || "#E5E7EB",
            textStyle: {
                color: theme?.primaryTextColor || "#F3F4F6",
            },
            axisPointer: {
                type: "cross",
            },
            position: function (point: any, _params: any, _dom: any, _rect: any, size: any) {
                return [10, point[1] - size.contentSize[1] / 2];
            },
            formatter: (params: any) => {
                if (!Array.isArray(params) || params.length === 0) return "";
                const monthIndex = params[0].dataIndex;
                const monthName = allMonths[monthIndex];
                const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);

                const realAcum = Math.round(realAcumuladoValues[monthIndex]);
                const realMonth = Math.round(realMesValues[monthIndex]);
                const metaAcum = Math.round(metaAcumuladaValues[monthIndex]);
                const metaMonth = Math.round(metaMesValues[monthIndex]);
                const metaTotal = Math.round(targetValues[monthIndex]);
                const total = realAcum + realMonth;

                return `
                    <div style="padding: 8px 12px; text-align: left;">
                        <strong>${capitalizedMonth}</strong><br/>
                        <div style="margin-top: 6px;">
                            <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:#3B6BA5;margin-right:6px;"></span>Energia acumulada: <strong>${realAcum.toLocaleString("pt-BR")} MWh</strong><br/>
                            <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:#7EB3E0;margin-right:6px;"></span>Energia m\u00eas: <strong>${realMonth.toLocaleString("pt-BR")} MWh</strong><br/>
                            <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:#FFF;margin-right:6px;border:1px solid #999;"></span>Energia acumulada meta: <strong>${metaAcum.toLocaleString("pt-BR")} MWh</strong><br/>
                            <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:#FFF;margin-right:6px;border:1px solid #999;"></span>Energia m\u00eas meta: <strong>${metaMonth.toLocaleString("pt-BR")} MWh</strong><br/>
                            <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:#FFF;margin-right:6px;border:1px solid #999;"></span>Energia total meta (YTD): <strong>${metaTotal.toLocaleString("pt-BR")} MWh</strong><br/>
                        </div>
                        <hr style="border-color:#555;margin:6px 0;"/>
                        <strong>Energia total (YTD): ${total.toLocaleString("pt-BR")} MWh</strong>
                    </div>
                `;
            },
        },
    };

    return (
        <Box
            sx={{
                width: "100%",
                padding: "16px",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
            }}
        >
            <Box
                ref={chartRef}
                sx={{
                    width: "100%",
                    height: 300,
                }}
            >
                <RopChart
                    option={chartOption}
                />
            </Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: 0,
                    gap: 5,
                    flexWrap: "wrap",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.8,
                    }}
                >
                    <Box
                        sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "3px",
                            backgroundColor: "#3B6BA5",
                        }}
                    />
                    <Typography
                        sx={{
                            color: theme.secondaryTextColor,
                            fontSize: "12px",
                            fontWeight: "400",
                        }}
                    >
                        Energia acumulada
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.8,
                    }}
                >
                    <Box
                        sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "3px",
                            backgroundColor: "#7EB3E0",
                        }}
                    />
                    <Typography
                        sx={{
                            color: theme.secondaryTextColor,
                            fontSize: "12px",
                            fontWeight: "400",
                        }}
                    >
                        Energia mês
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
